import React, { useState, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import Card from '../design-system/Card';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import useEnergyData, { yesterdayLocal } from '../hooks/useEnergyData';
import { colors, spacing, radius, fontStack } from '../design-system/tokens';

/**
 * Energy — reflection engine.
 *
 * Loop: Log → Explain → Done.
 *
 * The user logs yesterday's behavioral inputs (sleep, deep work, meals
 * skipped, movement, would-repeat) in under 10 seconds. On submit we
 * upsert a row into energy_logs (one per log_date) and call
 * /api/yesterday-analysis, which sends the row + last 14 entries to
 * Sonnet and persists the rendered reflection back to the row. The
 * reflection renders inline below the form on the same screen.
 *
 * Return state: if a row already exists with log_date === yesterday
 * and has analysis_text, we skip the form and show the cached
 * reflection. The "Re-enter yesterday" link returns to the form.
 *
 * The Phase-2 tracker (sliders + charts + scores) is archived to
 * `_archive/EnergyV2.old.js`.
 */
export default function Energy() {
  const { userId, yesterdayLog, last14Days, loading, error, refresh } = useEnergyData();

  // After a fresh submit we hold the just-returned analysis in local state
  // so the user sees it without a re-fetch round trip. Once `refresh()`
  // resolves, `yesterdayLog` will hold the same data.
  const [freshAnalysis, setFreshAnalysis] = useState(null);

  // User clicked "Re-enter yesterday" — force the form back into view even
  // if the row already exists.
  const [reEntering, setReEntering] = useState(false);

  const initialLoading = loading && last14Days.length === 0 && !error;

  const cachedAnalysis = useMemo(() => {
    if (!yesterdayLog?.analysis_text) return null;
    return {
      rendered_text: yesterdayLog.analysis_text,
      factors: yesterdayLog.analysis_structured?.factors || [],
      pattern: yesterdayLog.analysis_structured?.pattern || '',
      streak:  yesterdayLog.analysis_structured?.streak  || '',
      lever:   yesterdayLog.analysis_structured?.lever   || '',
    };
  }, [yesterdayLog]);

  const showAnalysis = !reEntering && (freshAnalysis || cachedAnalysis);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xl,
        maxWidth: 600,
        margin: '0 auto',
        padding: `${spacing.xl}px 0`,
        width: '100%',
      }}
    >
      <Header analysisShowing={!!showAnalysis} />

      {initialLoading ? (
        <LoadingState rows={3} label="Loading" />
      ) : showAnalysis ? (
        <ReflectionCard analysis={showAnalysis} />
      ) : (
        <ReflectionForm
          userId={userId}
          existing={yesterdayLog}
          onSaved={(analysis) => {
            setFreshAnalysis(analysis);
            setReEntering(false);
            refresh();
          }}
        />
      )}

      {showAnalysis && (
        <ResetRow onReset={() => setReEntering(true)} />
      )}

      {error && (
        <ErrorState
          title="Couldn't load your logs"
          message={error}
          onRetry={refresh}
          inline
        />
      )}
    </div>
  );
}

/* ================================================================
   Header
   ================================================================ */

function Header({ analysisShowing }) {
  return (
    <header style={{ fontFamily: fontStack }}>
      <h1
        style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 600,
          color: colors.textPrimary,
          letterSpacing: '-0.01em',
        }}
      >
        How was yesterday?
      </h1>
      <p
        style={{
          margin: '6px 0 0',
          fontSize: 14,
          color: colors.textSecondary,
          lineHeight: 1.5,
        }}
      >
        {analysisShowing
          ? 'Here’s what your inputs say.'
          : 'Five honest answers. One clear reflection.'}
      </p>
    </header>
  );
}

/* ================================================================
   Reflection form
   ================================================================ */

function ReflectionForm({ userId, existing, onSaved }) {
  // Pre-fill from the existing row if the user is re-entering. Sensible
  // defaults otherwise.
  const [sleep, setSleep]             = useState(existing?.sleep_hours      ?? 7.0);
  const [deepWork, setDeepWork]       = useState(existing?.deep_work_hours  ?? 2.0);
  const [meals, setMeals]             = useState(existing?.meals_skipped    ?? 0);
  const [movement, setMovement]       = useState(existing?.movement         ?? true);
  const [wouldRepeat, setWouldRepeat] = useState(existing?.would_repeat     ?? 'Mostly');
  const [submitting, setSubmitting]   = useState(false);
  const [errMsg, setErrMsg]           = useState(null);

  const handleSubmit = useCallback(async () => {
    if (!userId) {
      setErrMsg('Sign in to log yesterday.');
      return;
    }
    setSubmitting(true);
    setErrMsg(null);
    try {
      const log_date = yesterdayLocal();

      // Upsert on (user_id, log_date) so re-submitting the same day works.
      const { data: row, error: insErr } = await supabase
        .from('energy_logs')
        .upsert(
          {
            user_id: userId,
            log_date,
            sleep_hours: sleep,
            deep_work_hours: deepWork,
            meals_skipped: meals,
            movement,
            would_repeat: wouldRepeat,
            // Wipe stale analysis if re-entering — the function will
            // refill these.
            analysis_text: null,
            analysis_structured: null,
          },
          { onConflict: 'user_id,log_date' }
        )
        .select('id')
        .single();
      if (insErr) throw insErr;

      // Call the Netlify function. If it fails, surface the error so the
      // user can retry — the row is already saved.
      const fnResp = await fetch('/api/yesterday-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, log_id: row.id }),
      });
      const fnData = await fnResp.json().catch(() => null);
      if (!fnResp.ok || !fnData?.rendered_text) {
        throw new Error(fnData?.error || 'Analysis service is unavailable.');
      }
      onSaved && onSaved(fnData);
    } catch (e) {
      setErrMsg(e?.message || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [userId, sleep, deepWork, meals, movement, wouldRepeat, onSaved]);

  return (
    <Card padding={spacing.xl}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
        <NumericField
          label="Sleep"
          helper="hours last night"
          unit="hours"
          value={sleep}
          min={0}
          max={14}
          step={0.5}
          onChange={setSleep}
        />
        <NumericField
          label="Deep work"
          helper="focused hours yesterday"
          unit="hours"
          value={deepWork}
          min={0}
          max={12}
          step={0.5}
          onChange={setDeepWork}
        />
        <SegmentedField
          label="Meals skipped"
          helper="0, 1, or 2"
          value={meals}
          onChange={setMeals}
          options={[
            { label: '0', value: 0 },
            { label: '1', value: 1 },
            { label: '2', value: 2 },
          ]}
        />
        <SegmentedField
          label="Movement"
          helper="any walk, workout, or activity"
          value={movement ? 'yes' : 'no'}
          onChange={(v) => setMovement(v === 'yes')}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No',  value: 'no'  },
          ]}
        />

        {/* Would-you-repeat — emphasised: 1.5x spacing above, larger label,
            larger buttons, stronger selected state. */}
        <div style={{ marginTop: spacing.base /* +24 base gap = ~48 = 1.5x */ }}>
          <SegmentedField
            label="Would you repeat yesterday?"
            value={wouldRepeat}
            onChange={setWouldRepeat}
            emphasis
            options={[
              { label: 'Yes',    value: 'Yes'    },
              { label: 'Mostly', value: 'Mostly' },
              { label: 'No',     value: 'No'     },
            ]}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '14px 24px',
              minHeight: 48,
              borderRadius: 999,
              border: 'none',
              backgroundColor: submitting ? '#9CB1FF' : colors.primary,
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: fontStack,
              cursor: submitting ? 'wait' : 'pointer',
              transition: 'background-color 120ms ease',
            }}
          >
            {submitting ? 'Reflecting…' : 'See yesterday'}
          </button>
          {errMsg && (
            <span
              style={{
                fontSize: 13,
                color: 'var(--danger)',
                fontFamily: fontStack,
                textAlign: 'center',
              }}
            >
              {errMsg}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ================================================================
   Form fields
   ================================================================ */

function NumericField({ label, helper, unit, value, min, max, step, onChange }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: fontStack }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: 14,
              color: colors.textPrimary,
              fontWeight: 600,
            }}
          >
            {label}
          </span>
          {helper && (
            <span style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 400 }}>
              {helper}
            </span>
          )}
        </div>
        <span
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: colors.textPrimary,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {Number(value).toFixed(1)}{' '}
          <span style={{ fontSize: 12, fontWeight: 400, color: colors.textSecondary }}>
            {unit}
          </span>
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        style={{
          width: '100%',
          accentColor: colors.primary,
          cursor: 'pointer',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: colors.textSecondary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </label>
  );
}

function SegmentedField({ label, helper, value, onChange, options, emphasis = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: fontStack }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: emphasis ? 16 : 14,
            color: colors.textPrimary,
            fontWeight: emphasis ? 700 : 600,
            letterSpacing: emphasis ? '-0.005em' : 0,
          }}
        >
          {label}
        </span>
        {helper && (
          <span style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 400 }}>
            {helper}
          </span>
        )}
      </div>
      <div
        role="radiogroup"
        aria-label={label}
        style={{
          display: 'inline-flex',
          gap: emphasis ? 8 : 6,
          padding: emphasis ? 6 : 4,
          borderRadius: 999,
          backgroundColor: 'var(--bg-surface-2)',
          border: `1px solid ${colors.border}`,
          alignSelf: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              style={{
                minWidth: emphasis ? 96 : 56,
                padding: emphasis ? '12px 22px' : '8px 14px',
                borderRadius: 999,
                border: 'none',
                backgroundColor: selected ? colors.primary : 'transparent',
                color: selected ? '#fff' : colors.textPrimary,
                fontSize: emphasis ? 15 : 13,
                fontWeight: selected && emphasis ? 700 : 600,
                fontFamily: fontStack,
                cursor: 'pointer',
                boxShadow:
                  selected && emphasis
                    ? '0 4px 12px rgba(var(--primary-rgb) / 0.28)'
                    : 'none',
                transform: selected && emphasis ? 'translateY(-1px)' : 'none',
                transition:
                  'background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================
   Reflection card (after submit OR cached)
   ================================================================ */

function ReflectionCard({ analysis }) {
  const { rendered_text, factors, pattern, streak, lever } = analysis;

  if (!rendered_text) {
    return <EmptyState message="Reflection wasn't generated. Try again tomorrow." />;
  }

  return (
    <Card padding={spacing.lg} accentColor={colors.accentEnergy}>
      <p
        style={{
          margin: 0,
          fontSize: 17,
          lineHeight: 1.6,
          color: colors.textPrimary,
          fontFamily: fontStack,
        }}
      >
        {rendered_text}
      </p>

      {factors && factors.length > 0 && (
        <div
          style={{
            marginTop: spacing.base,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          {factors.map((f, i) => (
            <span
              key={i}
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: colors.textSecondary,
                padding: '4px 10px',
                borderRadius: 999,
                border: `1px solid ${colors.border}`,
                backgroundColor: 'var(--bg-surface-2)',
                fontFamily: fontStack,
              }}
            >
              {f}
            </span>
          ))}
        </div>
      )}

      {(pattern || streak) && (
        <div
          style={{
            marginTop: spacing.base,
            paddingTop: spacing.base,
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontFamily: fontStack,
            fontSize: 13,
            color: colors.textSecondary,
            lineHeight: 1.55,
          }}
        >
          {pattern && <div>{pattern}</div>}
          {streak  && <div>{streak}</div>}
        </div>
      )}

      {lever && (
        <div
          style={{
            marginTop: spacing.base,
            padding: '10px 14px',
            borderRadius: radius.sm,
            backgroundColor: 'var(--accent-energy-soft)',
            color: colors.textPrimary,
            fontFamily: fontStack,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <span style={{ fontWeight: 600 }}>For tomorrow: </span>
          {lever}
        </div>
      )}
    </Card>
  );
}

function ResetRow({ onReset }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', fontFamily: fontStack }}>
      <button
        type="button"
        onClick={onReset}
        style={{
          background: 'transparent',
          border: 'none',
          color: colors.textSecondary,
          fontSize: 13,
          fontFamily: fontStack,
          cursor: 'pointer',
          padding: '6px 0',
        }}
      >
        Re-enter yesterday
      </button>
    </div>
  );
}
